import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import {
  User,
  Trophy,
  Target,
  TrendingUp,
  ArrowRight,
  RefreshCw,
  Play,
  Award,
  Headphones,
  Calendar,
  BookOpen,
  Settings,
  Sparkles,
  Lock,
  CheckCircle2,
  Flame,
  Shield,
  Recycle,
  Sprout,
  Footprints,
  Leaf,
  Globe,
  Lightbulb,
} from "lucide-react";
import { toast } from "sonner";

const ICON_MAP = {
  BookOpen, Flame, Footprints, Shield, Recycle, Sprout, Leaf, Globe, Lightbulb, Target, Trophy,
};

const quizConfig = {
  climate_exposure: { name: "Exposition Climatique", color: "var(--terracotta)", link: "/", description: "Evaluez votre vulnerabilite face aux aleas" },
  circular_economy: { name: "Economie Circulaire", color: "var(--maquis)", link: "/design-durable", description: "Testez vos connaissances circulaires" },
  biomimicry: { name: "Biomimetisme", color: "var(--maquis-light)", link: "/innovation-responsable", description: "L'innovation inspiree de la nature" },
  carbon_footprint: { name: "Bilan Carbone", color: "var(--terracotta-dark)", link: "/strategie-climat", description: "Comprenez votre empreinte" },
  rse: { name: "Strategie RSE", color: "var(--dusty-rose)", link: "/strategie-rse", description: "Evaluez votre demarche RSE" },
};

const ModuleCard = ({ module, purchased, progress }) => {
  const IconComp = ICON_MAP[module.icon] || BookOpen;
  const pct = progress?.progress_percent || 0;

  return (
    <Link
      to={`/modules/${module.id}`}
      className="group relative card-premium p-6 flex flex-col transition-all hover:shadow-xl hover:-translate-y-2"
      data-testid={`module-card-${module.id}`}
    >
      {/* Color accent bar */}
      <div className="absolute top-0 left-0 right-0 h-1.5 rounded-t-[1.5rem]" style={{ backgroundColor: module.color }} />

      {/* Icon */}
      <div
        className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5 transition-transform group-hover:scale-110"
        style={{ backgroundColor: `${module.color}15`, color: module.color }}
      >
        <IconComp className="w-7 h-7" />
      </div>

      {/* Title & desc */}
      <h3 className="font-display text-lg font-bold text-[var(--charcoal)] mb-2 leading-snug">
        {module.title}
      </h3>
      <p className="text-sm text-[var(--stone)] leading-relaxed mb-4 flex-1">
        {module.description}
      </p>

      {/* Content stats */}
      <div className="flex items-center gap-3 text-xs text-[var(--stone)] mb-4">
        {module.video_count > 0 && (
          <span className="flex items-center gap-1"><Play className="w-3 h-3" /> {module.video_count} videos</span>
        )}
        {module.pdf_count > 0 && (
          <span className="flex items-center gap-1"><BookOpen className="w-3 h-3" /> {module.pdf_count} docs</span>
        )}
        {module.quiz_count > 0 && (
          <span className="flex items-center gap-1"><Target className="w-3 h-3" /> {module.quiz_count} quiz</span>
        )}
      </div>

      {/* Progress or CTA */}
      {purchased ? (
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs font-medium text-[var(--stone)]">Progression</span>
            <span className="text-xs font-bold" style={{ color: module.color }}>{pct}%</span>
          </div>
          <div className="h-2 bg-[var(--sand)] rounded-full overflow-hidden">
            <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: pct >= 100 ? "var(--maquis)" : module.color }} />
          </div>
          {pct >= 100 && (
            <div className="flex items-center gap-1 mt-2 text-xs text-[var(--maquis)]">
              <CheckCircle2 className="w-3 h-3" /> Complete
            </div>
          )}
        </div>
      ) : (
        <div className="flex items-center justify-between">
          <span className="text-sm font-semibold" style={{ color: module.color }}>
            {module.price > 0 ? `${module.price} EUR` : "Gratuit"}
          </span>
          <span className="inline-flex items-center gap-1 text-sm font-medium text-[var(--stone)] group-hover:text-[var(--terracotta)] transition-colors">
            Decouvrir <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </span>
        </div>
      )}
    </Link>
  );
};

export default function Dashboard() {
  const { user, token } = useAuth();
  const [searchParams] = useSearchParams();
  const [stats, setStats] = useState(null);
  const [modules, setModules] = useState([]);
  const [purchasedModules, setPurchasedModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("modules");

  useEffect(() => {
    if (searchParams.get("payment") === "success") {
      toast.success("Paiement reussi ! Bienvenue dans votre module.");
    }
  }, [searchParams]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [statsRes, modulesRes, purchasedRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/modules`),
        axios.get(`${API}/modules/purchased`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setStats(statsRes.data);
      setModules(modulesRes.data);
      setPurchasedModules(purchasedRes.data);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  useEffect(() => { fetchAll(); }, [token]);

  const getScorePercentage = (score, maxScore) => {
    if (!maxScore) return 0;
    return Math.round((score / maxScore) * 100);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--bone)] pt-32 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-[var(--terracotta)] border-t-transparent rounded-full animate-spin" />
          <p className="text-[var(--stone)]">Chargement de votre ecosysteme...</p>
        </div>
      </div>
    );
  }

  const completedQuizzes = Object.values(stats?.quiz_stats || {}).filter(Boolean).length;
  const purchasedIds = purchasedModules.map((m) => m.id);
  const totalModulesProgress = purchasedModules.length > 0
    ? Math.round(purchasedModules.reduce((sum, m) => sum + (m.progress?.progress_percent || 0), 0) / purchasedModules.length)
    : 0;

  return (
    <div className="min-h-screen bg-[var(--bone)] pt-28 pb-16" data-testid="dashboard-page">
      <div className="container-premium">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-10">
          <div>
            <p className="text-[var(--terracotta)] font-medium mb-1">Bonghjournu,</p>
            <h1 className="font-display text-4xl md:text-5xl font-bold text-[var(--charcoal)]">
              {user?.name}
            </h1>
            {user?.company && <p className="text-[var(--stone)] mt-1">{user.company}</p>}
          </div>
          <div className="flex items-center gap-3">
            {user?.is_admin && (
              <div className="flex items-center gap-3">
                <Link to="/admin/content" className="btn-primary flex items-center gap-2 text-sm" data-testid="admin-content-btn">
                  <Settings className="w-4 h-4" /> Gerer le contenu
                </Link>
                <Link to="/admin/modules" className="btn-secondary flex items-center gap-2 text-sm" data-testid="admin-modules-btn">
                  <Settings className="w-4 h-4" /> Gerer les modules
                </Link>
              </div>
            )}
            <button onClick={fetchAll} className="btn-secondary flex items-center gap-2 text-sm" data-testid="refresh-stats-btn">
              <RefreshCw className="w-4 h-4" /> Actualiser
            </button>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          <div className="card-premium p-5 text-center">
            <Trophy className="w-6 h-6 text-[var(--terracotta)] mx-auto mb-2" />
            <p className="font-display text-2xl font-bold text-[var(--charcoal)]">{stats?.total_quizzes_completed || 0}</p>
            <p className="text-xs text-[var(--stone)] uppercase tracking-wider">Quiz</p>
          </div>
          <div className="card-premium p-5 text-center">
            <TrendingUp className="w-6 h-6 text-[var(--maquis)] mx-auto mb-2" />
            <p className="font-display text-2xl font-bold text-[var(--charcoal)]">{stats?.average_score || 0}%</p>
            <p className="text-xs text-[var(--stone)] uppercase tracking-wider">Score moyen</p>
          </div>
          <div className="card-premium p-5 text-center">
            <BookOpen className="w-6 h-6 text-[var(--terracotta-dark)] mx-auto mb-2" />
            <p className="font-display text-2xl font-bold text-[var(--charcoal)]">{purchasedModules.length}</p>
            <p className="text-xs text-[var(--stone)] uppercase tracking-wider">Modules actifs</p>
          </div>
          <div className="card-premium p-5 text-center">
            <Target className="w-6 h-6 text-[var(--dusty-rose)] mx-auto mb-2" />
            <p className="font-display text-2xl font-bold text-[var(--charcoal)]">{totalModulesProgress}%</p>
            <p className="text-xs text-[var(--stone)] uppercase tracking-wider">Avancement</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 bg-[var(--sand)] p-1 rounded-2xl mb-10 max-w-lg">
          {[
            { id: "modules", label: "Centre d'Accompagnement", icon: Sparkles },
            { id: "quiz", label: "Mes Quiz", icon: Trophy },
            { id: "profil", label: "Profil", icon: User },
          ].map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-xl text-sm font-medium transition-all ${
                activeTab === id
                  ? "bg-white text-[var(--charcoal)] shadow-sm"
                  : "text-[var(--stone)] hover:text-[var(--charcoal)]"
              }`}
              data-testid={`tab-${id}`}
            >
              <Icon className="w-4 h-4" />
              <span className="hidden sm:inline">{label}</span>
            </button>
          ))}
        </div>

        {/* TAB: Centre d'Accompagnement */}
        {activeTab === "modules" && (
          <div>
            {/* Purchased modules (in progress) */}
            {purchasedModules.length > 0 && (
              <div className="mb-12">
                <h2 className="font-display text-2xl font-bold text-[var(--charcoal)] mb-6">
                  Vos modules en cours
                </h2>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {purchasedModules.map((m) => (
                    <ModuleCard key={m.id} module={m} purchased={true} progress={m.progress} />
                  ))}
                </div>
              </div>
            )}

            {/* All modules catalog */}
            <div>
              <div className="flex items-center gap-4 mb-6">
                <h2 className="font-display text-2xl font-bold text-[var(--charcoal)]">
                  Catalogue des modules
                </h2>
                <span className="badge-premium">{modules.length} disponibles</span>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {modules.map((m) => (
                  <ModuleCard
                    key={m.id}
                    module={m}
                    purchased={purchasedIds.includes(m.id)}
                    progress={purchasedModules.find((pm) => pm.id === m.id)?.progress}
                  />
                ))}
              </div>
            </div>

            {/* Podcast Banner */}
            <Link to="/podcast" className="block mt-12 group" data-testid="dashboard-podcast-banner">
              <div className="relative overflow-hidden rounded-3xl bg-[var(--maquis)] p-8 md:p-10 hover:shadow-2xl transition-all">
                <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
                <div className="absolute bottom-0 left-0 w-40 h-40 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />
                <div className="relative z-10 flex flex-col md:flex-row items-center gap-8">
                  {/* Cover */}
                  <div className="w-32 h-32 md:w-40 md:h-40 rounded-2xl overflow-hidden flex-shrink-0 shadow-2xl ring-4 ring-white/20 group-hover:ring-white/40 transition-all">
                    <img
                      src="https://image.ausha.co/LgFMa8OYmVnFcWrLy5J9bVKdN4WvkYAjwrA8wu6G_1400x1400.jpeg?t=1768570423"
                      alt="Maquis Vivant"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  {/* Info */}
                  <div className="flex-1 text-center md:text-left">
                    <div className="inline-flex items-center gap-2 bg-white/10 px-4 py-1.5 rounded-full text-sm text-[var(--bone)] mb-4">
                      <Headphones className="w-4 h-4" />
                      Podcast
                    </div>
                    <h3 className="font-display text-2xl md:text-3xl font-bold text-white mb-3">
                      Maquis Vivant
                    </h3>
                    <p className="text-white/70 mb-5 max-w-lg">
                      Le podcast pour batir une entreprise durable et responsable. Ecoutez les temoignages d'entrepreneurs engages.
                    </p>
                    <div className="flex flex-wrap items-center justify-center md:justify-start gap-3">
                      <span className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 px-5 py-2.5 rounded-full text-sm text-white font-medium transition-colors">
                        <Play className="w-4 h-4" /> Ecouter les episodes
                      </span>
                      <a href="https://open.spotify.com/show/52wol3hDjDzCs9KyYbX06j" target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="inline-flex items-center gap-2 bg-[#1DB954]/30 hover:bg-[#1DB954]/50 px-4 py-2.5 rounded-full text-sm text-white font-medium transition-colors">
                        Spotify
                      </a>
                      <a href="https://podcasts.apple.com/us/podcast/maquis-vivant-b%C3%A2tir-une-entreprise-durable-responsable/id1869635949" target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 px-4 py-2.5 rounded-full text-sm text-white font-medium transition-colors">
                        Apple Podcasts
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </Link>

            {/* Contact Card */}
            <Link to="/contact" className="card-premium p-6 group hover:shadow-xl mt-6 block">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[var(--terracotta)]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Calendar className="w-5 h-5 text-[var(--terracotta)]" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-[var(--charcoal)]">Accompagnement sur mesure</h3>
                  <p className="text-sm text-[var(--stone)]">
                    Besoin d'un accompagnement personnalise pour votre transition ?
                  </p>
                </div>
                <span className="inline-flex items-center gap-2 text-sm font-medium text-[var(--terracotta)] group-hover:gap-3 transition-all">
                  Prendre rendez-vous <ArrowRight className="w-4 h-4" />
                </span>
              </div>
            </Link>
          </div>
        )}

        {/* TAB: Mes Quiz */}
        {activeTab === "quiz" && (
          <div>
            <h2 className="font-display text-2xl font-bold text-[var(--charcoal)] mb-6">
              Vos resultats de quiz
            </h2>

            {/* Progress Ring */}
            <div className="card-premium p-8 mb-8">
              <div className="flex items-center gap-8">
                <div className="relative w-28 h-28 flex-shrink-0">
                  <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
                    <circle cx="50" cy="50" r="40" fill="none" stroke="var(--sand)" strokeWidth="8" />
                    <circle cx="50" cy="50" r="40" fill="none" stroke="var(--terracotta)" strokeWidth="8" strokeLinecap="round" strokeDasharray={`${(completedQuizzes / 5) * 251.2} 251.2`} />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="font-display text-2xl font-bold">{Math.round((completedQuizzes / 5) * 100)}%</span>
                  </div>
                </div>
                <div>
                  <h3 className="font-display text-xl font-semibold text-[var(--charcoal)] mb-2">Parcours de sensibilisation</h3>
                  <p className="text-[var(--stone)]">
                    {completedQuizzes === 5 ? "Tous les modules completes !" : `${5 - completedQuizzes} module(s) restant(s).`}
                  </p>
                  {completedQuizzes === 5 && (
                    <div className="flex items-center gap-2 text-[var(--maquis)] mt-2">
                      <Award className="w-5 h-5" /><span className="font-medium">Badge Explorateur obtenu !</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Quiz list */}
            <div className="space-y-4">
              {Object.entries(quizConfig).map(([key, config]) => {
                const quizStat = stats?.quiz_stats?.[key];
                const percentage = quizStat ? getScorePercentage(quizStat.best_score, quizStat.best_max) : null;
                return (
                  <Link key={key} to={config.link} className="card-premium p-6 flex items-center gap-4 group" data-testid={`quiz-result-${key}`}>
                    <div className="w-3 h-12 rounded-full flex-shrink-0" style={{ backgroundColor: config.color }} />
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-[var(--charcoal)]">{config.name}</h3>
                      {quizStat ? (
                        <div className="flex items-center gap-4 mt-2">
                          <div className="flex-1 h-2 bg-[var(--sand)] rounded-full overflow-hidden">
                            <div className="h-full rounded-full" style={{ width: `${percentage}%`, backgroundColor: config.color }} />
                          </div>
                          <span className="text-sm font-medium text-[var(--charcoal)] w-12">{percentage}%</span>
                        </div>
                      ) : (
                        <p className="text-sm text-[var(--stone)] mt-1">{config.description}</p>
                      )}
                    </div>
                    <ArrowRight className="w-5 h-5 text-[var(--stone)] group-hover:text-[var(--terracotta)] group-hover:translate-x-1 transition-all" />
                  </Link>
                );
              })}
            </div>
          </div>
        )}

        {/* TAB: Profil */}
        {activeTab === "profil" && (
          <div className="max-w-lg">
            <h2 className="font-display text-2xl font-bold text-[var(--charcoal)] mb-6">Mon profil</h2>
            <div className="card-premium p-8">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 bg-[var(--terracotta)] rounded-2xl flex items-center justify-center text-white font-display text-2xl font-bold">
                  {user?.name?.charAt(0)?.toUpperCase()}
                </div>
                <div>
                  <h3 className="font-display text-xl font-bold text-[var(--charcoal)]">{user?.name}</h3>
                  <p className="text-[var(--stone)]">{user?.email}</p>
                </div>
              </div>
              <div className="space-y-4">
                <div className="flex justify-between py-3 border-b border-[var(--sand)]">
                  <span className="text-[var(--stone)]">Entreprise</span>
                  <span className="font-medium text-[var(--charcoal)]">{user?.company || "—"}</span>
                </div>
                <div className="flex justify-between py-3 border-b border-[var(--sand)]">
                  <span className="text-[var(--stone)]">Modules actifs</span>
                  <span className="font-medium text-[var(--charcoal)]">{purchasedModules.length}</span>
                </div>
                <div className="flex justify-between py-3 border-b border-[var(--sand)]">
                  <span className="text-[var(--stone)]">Quiz completes</span>
                  <span className="font-medium text-[var(--charcoal)]">{stats?.total_quizzes_completed || 0}</span>
                </div>
                <div className="flex justify-between py-3">
                  <span className="text-[var(--stone)]">Score moyen</span>
                  <span className="font-medium text-[var(--charcoal)]">{stats?.average_score || 0}%</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
